import axios from 'axios'
import config from '@/config'
import {axiosWrapper} from '@/shared/api.utils'

const module = {
  namespaced: true,
  state: {
    user: null,
    users: null,
    healthcheck: null,
    featureFlags: null,
    notificationsOverview: null,
    allNotifications: null,
    lastCheckForNotifications: null,
    version: config.DOCKER_IMAGE_TAG,
  },
  mutations: {
    updateUser (state, payload) {
      console.log('updateuser')
      state.user = payload
    },
    updateFeatureFlags (state, payload) {
      console.log('updateFeatureFlags', payload)
      state.featureFlags = payload
    },
    healthcheckDidLoad (state, payload) {
      console.log('healthcheckDidLoad')
      state.healthcheck = payload
    },
    notificationsOverviewLoaded (state, payload) {
      console.log('notificationsOverviewLoaded')
      state.notificationsOverview = payload
      state.lastCheckForNotifications = new Date()
    },
    allNotificationsLoaded (state, payload) {
      console.log('allNotificationsLoaded')
      state.allNotifications = payload
    },
    setUsers (state, users) {
      // convert arr to dict for compatibility
      state.users = users ? Object.fromEntries(users.map(user => [user.user_id, user])) : null
    },
  },
  actions: {
    async getSuperuserCredentials (context, payload) {
      return axiosWrapper(
        axios.post(config.url + '/get_superuser_credentials/', {user_id: payload}))
    },
    getPolicyInformation (context, payload) {
      return new Promise((resolve, reject) => {
        axios.post(config.url + '/carrier-portal/get-user-policies/', { user_id: payload.userId, company_id: payload.companyId })
          .then((response) => {
            resolve(response)
          }, (e) => {
            reject(e)
          })
      })
    },
    async deleteUserWithAllData (context, payload) {
      return axiosWrapper(axios.delete(config.url + '/carrier-portal/users/', { data: payload }))
    },
    async unblockUser (context, userId) {
      return axiosWrapper(axios.post(`${config.url}/carrier-portal/users/${userId}/unblock/`, {}))
    },
    async deleteUserContact (context, payload) {
      return axiosWrapper(axios.delete(`${config.url}/carrier-portal/remove-user-contact/${payload.userId}/${payload.contactId}`))
    },
    async getAllInsureds (context, payload) {
      // retrieves all users and stores them to vuex. Quite slow for big values. 5k users loads in 5 seconds
      try {
        // huge page size to get all results. This call could be very slow
        let _payload = {companyId: payload, page_size: 100000}
        let result = await context.dispatch('getInsureds', _payload)
        context.commit('setUsers', result.search_result.items)
      } catch (e) {
        console.warn('Error during users fetch', e)
        context.commit('setUsers', null)
        throw e
      }
    },
    async getInsureds (context, payload) {
      try {
        let companyId = payload.companyId
        let params = {...payload}
        delete params['companyId']
        let result = await axiosWrapper(axios.get(`${config.url}/company/${companyId}/users/`, {params: params}))
        return result.data
      } catch (e) {
        console.warn('Error during users fetch', e)
        throw e
      }
    },
    async sendInsuredsListOnEmail (context, payload) {
      let params = {...payload}
      let companyId = params.companyId
      delete params['companyId']
      return axiosWrapper(axios.post(`${config.url}/company/${companyId}/send-insureds-xls-on-email/`, payload))
    },
    async loadFeatureFlags (context, payload) {
      let response = await axios.get(`${config.url}/feature-flags/`)
      context.commit('updateFeatureFlags', response.data)
    },
    async loadHealthcheck (context, payload) {
      let response = await axios.post(`${config.url}/up/`)
      context.commit('healthcheckDidLoad', response.data.britecores)
    },
    async loadNotificationsOverview (context, payload) {
      // this will reload every 60 seconds after any navigation action.
      let shouldUpdate = false
      // debounce
      if (!context.state.lastCheckForNotifications) {
        shouldUpdate = true
      } else {
        let secondSinceLastUpdate = Math.abs((new Date().getTime() - context.state.lastCheckForNotifications.getTime()) / 1000)
        if (secondSinceLastUpdate > 60) {
          shouldUpdate = true
        }
      }

      if (!shouldUpdate) {
        return
      }
      let response = await axios.get(`${config.url}/carrier-portal/notifications-overview/`)
      context.commit('notificationsOverviewLoaded', response.data)
    },
    async loadAllNotifications (context, payload) {
      let response = await axios.get(`${config.url}/carrier-portal/notifications/`)
      context.commit('allNotificationsLoaded', response.data)
    },
  },
  getters: {
    availableCompanies: state => {
      if (!state.user) {
        return []
      }
      
      return state.user.available_companies
    },
    isEdeliveryEnabledInCompany: (state) => (companyId) => {
      if (!state.user || !state.user.available_companies) {
        return false
      }

      return state.user.available_companies.find(_ => _.id === companyId).has_edelivery_enabled
    },
    isFeatureEnabled: (state) => (featureName) => {
      if (!state.featureFlags) {
        return false
      }

      return state.featureFlags.indexOf(featureName) >= 0
    },
    isSuperuser: state => {
      return state.user && state.user.is_superuser
    },
    isUserAllowedToMakeSetupChanges: state => {
      if (state.user.is_superuser) {
        return true
      }
      return state.user && state.user.superuser_login
    },
    healthcheckByCompany: (state) => (companyId) => {
      if (!companyId || !state.healthcheck) {
        return null
      }
      return state.healthcheck.find(_ => _.id === companyId)
    },
    companyNameById: (state) => (companyId) => {
      if (!companyId) {
        return null
      }
      return state.user.available_companies.find(_ => _.id === companyId).name
    },
    companyById: (state) => (companyId) => {
      if (!companyId) {
        return null
      }
      return state.user.available_companies.find(_ => _.id === companyId)
    },
  }
}

export default module
